import { useCallback, useState } from 'react';
import { Prompt } from 'react-router-dom';
import useLayoutScroll from 'App/components/AppLayout/useLayoutScroll';
import { useNavegate } from 'App/hooks';
import { toStatusType } from 'App/rtk/helpers';
import {
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  ActionsDesktop,
  ActionProp,
  CancelAndSaveButtons,
} from 'commons/components';
import { useToastStatus } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { TransferProductVariant } from 'domains/Catalog/Transfers/utils';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import {
  ProductsTransferCard,
  SourceDestinationCard,
  StockAlert,
  ZeroQuantityModal,
} from './components';
import { useCreateTransferMutation } from '../../transfersApi';
import SourceDestinationCardSkeleton from '../components/SourceDestinationCardSkeleton';

function NewTransferPage() {
  const t = useTranslationCatalog();
  const { goBack } = useNavegate();
  const { scrollToTop } = useLayoutScroll();
  const { locations, isLoading: isLoadingLocations } = useLocations();
  const [createTransfer, { status, data }] = useCreateTransferMutation();

  const [sourceLocationId, setSourceLocationId] = useState<string>();
  const [destinationLocationId, setDestinationLocationId] = useState<string>();
  const [productVariants, setProductVariants] = useState<
    TransferProductVariant[]
  >([]);

  const [showStockAlert, setShowStockAlert] = useState(false);
  const [showZeroQuantityModal, setShowZeroQuantityModal] = useState(false);
  const [showZeroQuantityAsWarning, setShowZeroQuantityAsWarning] =
    useState(false);

  const handleSelectSource = (selectedLocation: string) => {
    setSourceLocationId(selectedLocation);
    if (destinationLocationId === selectedLocation) {
      setDestinationLocationId(undefined);
    }
    if (locations.length === 2) {
      setDestinationLocationId(
        locations.find((location) => location.id !== selectedLocation)?.id,
      );
    }
  };

  const handleSelectDestination = (selectedLocation: string) => {
    setDestinationLocationId(selectedLocation);
    if (sourceLocationId === selectedLocation) {
      setSourceLocationId(undefined);
    }
    if (locations.length === 2) {
      setSourceLocationId(
        locations.find((location) => location.id !== selectedLocation)?.id,
      );
    }
  };

  const handleCancel = () => {
    goBack();
  };

  const handleSave = () => {
    setShowStockAlert(false);

    const hasSufficientStock = productVariants.every(
      (variant) => (variant.quantity ?? 0) <= (variant.stock ?? 0),
    );
    if (!hasSufficientStock) {
      scrollToTop('smooth');
      setShowStockAlert(true);
      return;
    }

    const hasZeroQuantityVariants = productVariants.some(
      (variant) => (variant.quantity ?? 0) === 0,
    );
    if (hasZeroQuantityVariants) {
      setShowZeroQuantityModal(true);
      return;
    }

    performSave();
  };

  const performSave = async () => {
    createTransfer({
      originLocationId: sourceLocationId || '',
      destinationLocationId: destinationLocationId || '',
      transferMode: 'QUANTITY',
      details: productVariants
        .filter((variant) => variant.quantity && variant.quantity > 0)
        .map((variant) => ({
          productId: Number(variant.productId),
          variantId: Number(variant.variantId),
          quantity: variant.quantity || 0,
        })),
    });
  };

  const createStatus = toStatusType(status);

  useToastStatus({
    progress: t('transfers.new.saveStatus.saving'),
    error: t('transfers.new.saveStatus.error'),
    status: createStatus,
    onSuccess: useCallback(() => {
      console.log('onSuccess:', data);
      goBack();
    }, [data, goBack]),
  });

  const handleTransferQuantityChange = (
    productId: string,
    variantId: string,
    quantity: number,
  ) => {
    setProductVariants((prev) =>
      prev.map((product) =>
        product.productId === productId && product.variantId === variantId
          ? { ...product, quantity }
          : product,
      ),
    );
  };

  const handleRemoveProduct = (productId: string, variantId: string) => {
    setProductVariants((prev) =>
      prev.filter(
        (product) =>
          !(product.productId === productId && product.variantId === variantId),
      ),
    );
  };

  const handleTransferTotalStock = (productId: string) => {
    setProductVariants((prev) =>
      prev.map((product) =>
        product.productId === productId
          ? { ...product, quantity: product.stock || 0 }
          : product,
      ),
    );
  };

  const handleApplyValuesToAllVariants = (variantId: string) => {
    const variant = productVariants.find(
      (product) => product.variantId === variantId,
    );
    if (!variant) return;
    const { productId, quantity } = variant;

    setProductVariants((prev) =>
      prev.map((product) =>
        product.productId === productId ? { ...product, quantity } : product,
      ),
    );
  };

  const handleZeroQuantityModalConfirm = () => {
    setShowZeroQuantityModal(false);
    performSave();
  };

  const handleZeroQuantityModalCancel = () => {
    setShowZeroQuantityAsWarning(true);
    setShowZeroQuantityModal(false);
  };

  const hasSomeProductsSelected = productVariants.some(
    (variant) => variant.quantity && variant.quantity > 0,
  );

  const isSaveDisabled = !hasSomeProductsSelected || !destinationLocationId;

  const saveAction: ActionProp = {
    children: t('transfers.new.save'),
    appearance: 'primary',
    onClick: handleSave,
    disabled: isSaveDisabled,
  };

  const cancelAction: ActionProp = {
    children: t('transfers.new.cancel'),
    appearance: 'default',
    onClick: handleCancel,
  };

  const desktopActions: ActionProp[] = [cancelAction, saveAction];

  const handleAddProducts = (productsToAdd: TransferProductVariant[]) => {
    setProductVariants((prevProducts) => [...prevProducts, ...productsToAdd]);
  };

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        <HeaderTop navigation={{ onClick: goBack }} mainAction={saveAction} />
      }
      headerContent={
        <HeaderContent
          title={t('transfers.new.title')}
          actions={<ActionsDesktop actions={desktopActions} />}
        />
      }
    >
      {isLoadingLocations && <SourceDestinationCardSkeleton />}
      <StockAlert
        isVisible={showStockAlert}
        onClose={() => setShowStockAlert(false)}
      />
      <ZeroQuantityModal
        show={showZeroQuantityModal}
        onConfirm={handleZeroQuantityModalConfirm}
        onCancel={handleZeroQuantityModalCancel}
      />
      {!isLoadingLocations && (
        <SourceDestinationCard
          sourceLocationId={sourceLocationId}
          destinationLocationId={destinationLocationId}
          onSelectSource={handleSelectSource}
          onSelectDestination={handleSelectDestination}
          disabledSource={hasSomeProductsSelected}
        />
      )}
      {!!sourceLocationId && (
        <ProductsTransferCard
          onAddProducts={handleAddProducts}
          productVariants={productVariants}
          sourceLocationId={sourceLocationId}
          showZeroQuantityAsWarning={showZeroQuantityAsWarning}
          onTransferQuantityChange={handleTransferQuantityChange}
          onRemoveProduct={handleRemoveProduct}
          onTransferTotalStock={handleTransferTotalStock}
          onApplyValuesToAllVariants={handleApplyValuesToAllVariants}
        />
      )}
      {!!sourceLocationId && (
        <CancelAndSaveButtons
          onCancel={handleCancel}
          onSave={handleSave}
          cancelText={t('transfers.new.cancel')}
          saveText={t('transfers.new.save')}
          isLoading={createStatus === 'loading'}
          isDisabled={isSaveDisabled}
        />
      )}
      <Prompt
        when={createStatus !== 'success' && productVariants.length > 0}
        message={t('common:exitEdit.info')}
      />
    </IonPageStratus>
  );
}

export default NewTransferPage;
